import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { X, Star, Clock, ChevronRight, ShoppingBag, Plus, Sparkles } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";

/**
 * Visual results panel for AI search/voice. NOT a chat — pure cards.
 * Modes:
 *   - discovery: store cards + item cards
 *   - filter: store cards filtered by speed
 *   - order: order_items present → auto-fill cart confirmation
 */
export const AISearchResults = ({ open, data, onClose, onAgain }) => {
    const navigate = useNavigate();
    const { add } = useCart();

    const totalOrder = useMemo(() => {
        if (!data?.order_items?.length) return 0;
        return data.order_items.reduce((s, i) => s + i.price * (i.quantity || 1), 0);
    }, [data]);

    if (!open || !data) return null;

    const { mode, reply, stores = [], items = [], order_items = [], max_minutes, transcript } = data;
    const isOrderMode = mode === "order" && order_items.length > 0;
    const isFilterMode = mode === "filter";

    const confirmOrder = () => {
        // Group order items by store; if multiple stores, just add all and route to first store
        order_items.forEach((it) => {
            const suffix = it.modifiers?.length ? ` · ${it.modifiers.join(", ")}` : "";
            const variantId = it.modifiers?.length
                ? `${it.id}-mod-${it.modifiers.join("_").replace(/\s+/g, "")}`
                : it.id;
            for (let i = 0; i < (it.quantity || 1); i++) {
                add({
                    id: variantId,
                    name: it.name + suffix,
                    price: it.price,
                    image_url: it.image_url,
                    category: it.category,
                }, { silent: true });
            }
        });
        const totalQty = order_items.reduce((s, i) => s + (i.quantity || 1), 0);
        toast.success(`Έγιναν ${totalQty} προϊόντα στο καλάθι.`);
        onClose();
        navigate("/checkout");
    };

    const addItemToCart = (it) => {
        add({
            id: it.id,
            name: it.name,
            price: it.price,
            image_url: it.image_url,
            category: it.category,
        });
    };

    const goToStore = (slug) => {
        if (!slug) return;
        onClose();
        navigate(`/store/${slug}`);
    };

    return (
        <div data-testid="ai-results" className="fixed inset-0 z-[750] bg-black/90 backdrop-blur-lg animate-fade-up overflow-y-auto">
            <div className="min-h-full flex flex-col">
                {/* Header */}
                <header className="sticky top-0 z-10 cyber-bg/95 backdrop-blur-xl border-b border-[var(--gold)]/20 px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                        <Sparkles size={16} className="text-[var(--gold-light)] shrink-0" />
                        <div className="min-w-0">
                            <p className="text-[9px] font-mono uppercase tracking-[0.3em] text-white/40">// ai search</p>
                            {transcript && (
                                <p className="text-xs text-white/70 italic truncate max-w-[60vw]">"{transcript}"</p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="ai-results-close"
                        className="h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white/80"
                    >
                        <X size={16} />
                    </button>
                </header>

                <div className="flex-1 px-5 py-5 max-w-2xl mx-auto w-full">
                    {/* AI reply */}
                    {reply && (
                        <div data-testid="ai-reply" className="glass-gold rounded-2xl px-4 py-3 mb-5 border border-[var(--gold)]/25 flex items-center gap-3">
                            <div className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-[var(--gold)] to-[#c98e15] grid place-items-center text-[10px] font-mono font-black text-[#0B0B0F]">
                                AI
                            </div>
                            <p className="text-sm text-white/90 leading-relaxed">{reply}</p>
                        </div>
                    )}

                    {isFilterMode && max_minutes && (
                        <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--gold)]/15 border border-[var(--gold)]/30">
                            <Clock size={12} className="text-[var(--gold-light)]" />
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold-light)]">
                                Παράδοση ≤ {max_minutes}'
                            </span>
                        </div>
                    )}

                    {/* DIRECT ORDER MODE */}
                    {isOrderMode && (
                        <section className="mb-6">
                            <h3 className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/50 mb-3">// auto-cart</h3>
                            <div className="space-y-2.5">
                                {order_items.map((it, i) => (
                                    <article
                                        key={i}
                                        data-testid={`order-item-${i}`}
                                        className="flex gap-3 items-center glass-gold rounded-2xl p-3 border border-[var(--gold)]/30"
                                    >
                                        <img src={it.image_url} alt={it.name} className="w-14 h-14 object-cover rounded-xl shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-bold text-white truncate">
                                                <span className="text-[var(--gold-light)]">{it.quantity}×</span> {it.name}
                                            </p>
                                            <p className="text-[10px] text-white/50 truncate">{it.store_name}</p>
                                            {it.modifiers?.length > 0 && (
                                                <p className="text-[10px] text-[var(--gold-light)] italic mt-0.5">
                                                    {it.modifiers.join(" · ")}
                                                </p>
                                            )}
                                        </div>
                                        <span className="font-mono text-sm text-[var(--gold-light)] shrink-0">
                                            €{(it.price * it.quantity).toFixed(2)}
                                        </span>
                                    </article>
                                ))}
                            </div>

                            <div className="mt-4 flex items-center justify-between glass-gold rounded-2xl px-4 py-3 border border-[var(--gold)]/30">
                                <span className="text-xs font-mono uppercase tracking-widest text-white/60">Σύνολο</span>
                                <span className="font-mono text-lg text-[var(--gold-light)] font-bold">€{totalOrder.toFixed(2)}</span>
                            </div>

                            <button
                                onClick={confirmOrder}
                                data-testid="ai-confirm-order"
                                className="gold-cta mt-4 w-full h-14 rounded-2xl flex items-center justify-between px-5 font-heading uppercase tracking-wider text-sm font-black active:scale-[0.98] transition"
                            >
                                <span className="flex items-center gap-2">
                                    <ShoppingBag size={18} strokeWidth={2.5} />
                                    <span>Να προχωρήσω στην παραγγελία;</span>
                                </span>
                                <ChevronRight size={20} strokeWidth={3} />
                            </button>

                            <button
                                onClick={onAgain}
                                data-testid="ai-cancel-order"
                                className="mt-2 w-full h-11 rounded-2xl border border-white/15 hover:border-white/30 text-xs font-mono uppercase tracking-widest text-white/70 transition"
                            >
                                Όχι, αλλιώς
                            </button>
                        </section>
                    )}

                    {/* STORES */}
                    {stores.length > 0 && (
                        <section className="mb-6">
                            <h3 className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/50 mb-3">
                                // {isFilterMode ? "γρήγορα" : "καταστήματα"} · {stores.length}
                            </h3>
                            <div className="space-y-2.5">
                                {stores.map((s) => (
                                    <article
                                        key={s.id}
                                        data-testid={`ai-store-${s.id}`}
                                        onClick={() => goToStore(s.slug)}
                                        className="cursor-pointer glass-gold rounded-2xl overflow-hidden flex border border-white/10 hover:border-[var(--gold)]/40 transition active:scale-[0.99]"
                                    >
                                        <img src={s.image_url} alt={s.name} className="w-24 h-24 object-cover shrink-0" />
                                        <div className="flex-1 p-3 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <h4 className="font-heading text-base text-white truncate" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                                                    {s.name}
                                                </h4>
                                                {s.is_hot && (
                                                    <span className="text-[8px] font-bold uppercase tracking-widest border border-[var(--gold)] text-[var(--gold-light)] px-1.5 py-0.5 rounded-full shrink-0">
                                                        HOT
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-[10px] text-white/50 capitalize">{s.cuisine}</p>
                                            <div className="flex items-center gap-3 mt-1.5 text-[10px]">
                                                <span className="flex items-center gap-1 text-[var(--gold-light)]">
                                                    <Star size={10} fill="currentColor" />
                                                    <span className="font-bold">{(s.rating || 0).toFixed(1)}</span>
                                                </span>
                                                <span className="flex items-center gap-1 text-white/60">
                                                    <Clock size={10} />
                                                    {s.delivery_minutes}'
                                                </span>
                                                <span className="text-white/50">€{s.delivery_fee.toFixed(2)}</span>
                                            </div>
                                        </div>
                                        <div className="grid place-items-center px-3 text-white/40">
                                            <ChevronRight size={18} />
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* ITEMS */}
                    {items.length > 0 && !isOrderMode && (
                        <section className="mb-6">
                            <h3 className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/50 mb-3">
                                // πιάτα · {items.length}
                            </h3>
                            <div className="grid grid-cols-2 gap-2.5">
                                {items.map((it) => (
                                    <article
                                        key={it.id}
                                        data-testid={`ai-item-${it.id}`}
                                        className="glass-gold rounded-2xl overflow-hidden border border-white/10 hover:border-[var(--gold)]/40 transition flex flex-col"
                                    >
                                        <div
                                            onClick={() => goToStore(it.store_slug)}
                                            className="cursor-pointer"
                                        >
                                            <img src={it.image_url} alt={it.name} className="w-full h-28 object-cover" />
                                            <div className="p-2.5">
                                                <p className="text-[9px] font-mono uppercase tracking-widest text-[var(--gold-light)]/70 truncate">
                                                    {it.store_name}
                                                </p>
                                                <h4 className="text-sm font-bold text-white truncate mt-0.5">{it.name}</h4>
                                                <p className="text-[10px] text-white/50 line-clamp-2 mt-0.5 min-h-[24px]">
                                                    {it.description}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="px-2.5 pb-2.5 flex items-center justify-between mt-auto">
                                            <span className="font-mono text-sm text-[var(--gold-light)] font-bold">€{it.price.toFixed(2)}</span>
                                            <button
                                                onClick={() => addItemToCart(it)}
                                                data-testid={`ai-add-item-${it.id}`}
                                                className="h-7 w-7 grid place-items-center rounded-full bg-[var(--gold)] text-[#0B0B0F] hover:scale-110 transition"
                                            >
                                                <Plus size={14} strokeWidth={3} />
                                            </button>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* EMPTY */}
                    {!isOrderMode && stores.length === 0 && items.length === 0 && (
                        <div data-testid="ai-empty" className="text-center py-16">
                            <p className="text-sm text-white/60">Δεν βρήκα κάτι. Δοκίμασε αλλιώς.</p>
                            <button
                                onClick={onAgain}
                                className="mt-4 px-5 h-10 rounded-full glass-gold border border-[var(--gold)]/30 text-xs font-mono uppercase tracking-widest text-[var(--gold-light)]"
                            >
                                Πάλι
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
